import { FocusEvent, MouseEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { debounce } from 'underscore';
import TextAreaAutosize from 'react-textarea-autosize';

import { GENERAL_EDITABLE_PERMISSIONS } from '@/types/permissions.types';
import useHasPermission from '@/utils/authentication/use-has-permission';
import { mergeRefs } from '@/utils/merge-refs';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';
import { TEditStateTextAreaWrapperProps } from './edit-state-wrapper.types';

import styles from './edit-state-wrapper.module.css';

const EditStateTextAreaWrapper = ({
  'data-component': dataComponent,
  onBlur,
  onDebounce,
  debounceTime = 250,
  textAreaProps = {},
  maxRowsFocused: maxRowsFocusedProps,
  maxRowsBlurred: maxRowsBlurredProps,
  preFirstTimeOpenHeight = '40px',
}: TEditStateTextAreaWrapperProps) => {
  const {
    defaultValue,
    maxLength = 64000,
    disabled,
    maxRows: maxRowsProps,
    minRows = 1,
    onFocus,
    onBlurCapture,
    itemRef,
    style,
    className,
    onChange,
    placeholder = '',
    value,
    ...restOfTextAreaProps
  } = textAreaProps;

  const maxRowsFocused = maxRowsFocusedProps || maxRowsProps || 2;
  const maxRowsBlurred = maxRowsBlurredProps || 2;
  /* technically `!!maxRowsBlurred` won't ever be false, but using it for future extendability */
  const truncateOnBlur = !!maxRowsBlurred;

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const readOnlyTextAreaRef = useRef<HTMLTextAreaElement>(null);

  const shouldScroll = useRef(!!truncateOnBlur);
  const readOnlyContainerRef = useRef<HTMLDivElement>(null);
  const readOnlyContentRef = useRef<HTMLSpanElement | null>(null);

  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(defaultValue || value || '');
  const [inputOffsetHeight, setInputOffsetHeight] = useState(0);

  const { hasPermission: hasPermissions } = useHasPermission({
    allowedRoles: GENERAL_EDITABLE_PERMISSIONS,
  });

  const handleDebounce = useRef(
    debounce((value: string) => {
      onDebounce?.(value);
    }, debounceTime)
  ).current;

  const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
    e.target.value = e.target.value.trim(); // overwrite the input value to remove spaces
    onBlur?.(e.target.value);
  };

  // e is button or div click event
  const forceFocus = (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    if (!textAreaRef.current || !hasPermissions) return;
    // if e.target is the input, do nothing to prevent resetting the cursor
    if (e.target === textAreaRef.current) {
      return;
    }

    textAreaRef.current.focus();

    // scroll to end - see BDEV-1632
    textAreaRef.current.scrollTo({ top: textAreaRef.current.scrollHeight });

    // set the cursor to the end of the text - see BDEV-1632
    textAreaRef.current.setSelectionRange(textAreaRef.current.value.length, textAreaRef.current.value.length);
  };

  const scrollToTopOfReadOnlyInput = useCallback(() => {
    if (!shouldScroll.current) return;
    const readOnlyTextArea = readOnlyTextAreaRef.current;
    if (readOnlyTextArea) {
      readOnlyTextArea.scrollTo({ top: 0 });
    }
  }, []);

  const scrollToTopOfEditableInput = useCallback(() => {
    if (!shouldScroll.current) return;
    const textArea = textAreaRef.current;
    if (textArea) {
      textArea.scrollTo({ top: 0 });
    }
  }, []);

  // set the height of the read-only input to match the editable input
  useLayoutEffect(() => {
    const textArea = textAreaRef.current;
    if (!textArea) return;
    setInputOffsetHeight(textArea?.offsetHeight || 0);

    // need textAreaRef.current
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textAreaRef.current]);

  // update local state if parent changes value
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  return (
    <div
      className={classNames(styles.textAreaWrapper, !hasPermissions && styles.textAreaDisabled)}
      /*
        intentional onClick on div, do not change this to a button or any other tabbable element
        because the nested textarea itself is tabbable
      */
      onClick={forceFocus}
      data-component={dataComponent || 'EditStateTextAreaWrapper'}
    >
      <SlideDownCard animateOnInitialLoad={false} autoGrow open height={preFirstTimeOpenHeight}>
        <Container
          width="100%"
          className={`radius-8 overflow-hidden position-relative padding-v-6 ${styles.textAreaContainer}`}
          transitionDuration="0s"
        >
          <TextAreaAutosize
            onBlur={handleBlur}
            className={`clear-textarea flex-column S3 ${className || ''}`}
            minRows={minRows}
            maxRows={isFocused ? maxRowsFocused : maxRowsBlurred} // reduce height when not focused
            placeholder={placeholder}
            defaultValue={defaultValue}
            disabled={disabled || !hasPermissions}
            maxLength={maxLength}
            onChange={(e) => {
              setLocalValue(e.target.value);
              onChange?.(e);
              handleDebounce?.(e.target.value);
            }}
            onFocus={(e) => {
              setIsFocused(true);
              if (truncateOnBlur && textAreaRef.current) {
                shouldScroll.current = true;
                scrollToTopOfReadOnlyInput();
                shouldScroll.current = false;
              }
              onFocus?.(e);
            }}
            onBlurCapture={(e) => {
              setIsFocused(false);
              if (truncateOnBlur) {
                shouldScroll.current = true;
                scrollToTopOfReadOnlyInput();
                scrollToTopOfEditableInput();
                shouldScroll.current = false;
              }
              onBlurCapture?.(e);
            }}
            ref={mergeRefs(textAreaRef, itemRef)}
            style={{
              ...(truncateOnBlur
                ? {
                    opacity: isFocused ? 1 : 0,
                  }
                : {}),
              ...style,
            }}
            value={value}
            {...restOfTextAreaProps}
          />
          {truncateOnBlur ? (
            <Container
              className={`position-absolute top-0 pointer-events-none ${!localValue && !defaultValue ? styles.fakePlaceholderContainer : ''}`}
              borderRadius="8px"
              width="100%"
              // when textarea is not focused, the height should be only maxRowsBlurred lines
              height={`${inputOffsetHeight || textAreaRef.current?.offsetHeight || 16 * maxRowsBlurred}px`}
              style={{
                opacity: isFocused ? 0 : 1,
              }}
              transitionDuration="0s"
              ref={readOnlyContainerRef}
            >
              <div className="padding-v-6">
                <Text clamp={maxRowsBlurred} className="S3 margin-r-4" whiteSpace="pre-wrap" height="100%">
                  {localValue || defaultValue ? (
                    <span className="height-100-percent width-100-percent" ref={readOnlyContentRef}>
                      {(localValue || defaultValue || '').toString().trim()}
                    </span>
                  ) : null}

                  {!localValue && !defaultValue && hasPermissions ? (
                    <Text className="S3 margin-t-0 margin-b-auto" height="100%">
                      {placeholder}
                    </Text>
                  ) : null}
                </Text>
              </div>
            </Container>
          ) : null}
        </Container>
      </SlideDownCard>
    </div>
  );
};

export default EditStateTextAreaWrapper;
